import getProductKey from 'helper/requests/getProductKey';
import {
  transformDecompOptionsResponse,
  transformDecompInformeDiarioResponse,
  transformDecompPremissasResponse,
  transformDecompCargaResponse,
  transformDecompGeracaoResponse,
  transformDecompResultadosResponse,
  transformDecompVisaoGeralResponse,
  transformDecompEmpilhamentoResponse,
  transformDecompChuvaResponse,
} from 'helper/Relatorios/Decomp';
import {
  transformDessemOptionsResponse,
  transformDessemPremissasResponse,
  transformDessemPremissasEdicaoResponse,
  transformDessemCargaResponse,
  transformDessemCargaLiquidaResponse,
  transformDessemGeracaoRenovavelResponse,
  transformDessemVisaoGeralResponse,
  transformDessemResultadosResponse,
} from 'helper/Relatorios/Dessem';
import { transformAprovacaoResponse, transformCalendarioResponse } from 'helper/Relatorios/Aprovacao';
import {
  transformIntersemanalVisaoGeralResponse,
  transformIntersemanalChuvaResponse,
  transformIntersemanalCargaResponse,
  transformIntersemanalResultadosResponse,
  transformIntersemanalPremissasResponse,
} from 'helper/Relatorios/Intersemanal';
import {
  transformSemanalVisaoGeralResponse,
  transformSemanalChuvaResponse,
  transformSemanalCargaResponse,
  transformSemanalResultadosResponse,
  transformSemanalPremissasResponse,
} from 'helper/Relatorios/Semanal';
import { transformMatrizChuvaObservadaResponse } from 'helper/Relatorios/Matriz';
import {
  transformMensalVisaoGeralResponse,
  transformMensalChuvaResponse,
  transformMensalSimilaridadeClimaticaResponse,
  transformMensalCargaResponse,
  transformMensalResultadosResponse,
  transformMensalPremissasResponse,
} from 'helper/Relatorios/Mensal';
import {
  DecompOptions,
  DecompInformeDiario,
  DecompPremissas,
  DecompPremissasParams,
  DecompReportParams,
  DecompCarga,
  DecompGeracao,
  DecompResultados,
  DecompVisaoGeral,
  DecompEmpilhamento,
  DecompChuva,
  DecompSecaoParams,
} from 'types/Relatorios/Decomp';
import {
  DessemOptions, DessemOptionsParams, DessemPremissas, DessemCarga, DessemCargaLiquida,
  DessemGeracaoRenovavel, DessemVisaoGeral, DessemResultados, DessemSecaoParams,
  DessemPremissasEdicao, DessemEditarPremissasBody, DessemRestaurarPremissasBody,
} from 'types/Relatorios/Dessem';
import {
  AprovacaoParams, AprovacaoSecoesBody, AprovacaoStatus, Calendario, CalendarioParams,
  RelatorioPathInforme,
} from 'types/Relatorios/Aprovacao';
import {
  IntersemanalVisaoGeral,
  IntersemanalVisaoGeralParams,
  IntersemanalChuva,
  IntersemanalCarga,
  IntersemanalResultados,
  IntersemanalPremissas,
  IntersemanalSecaoParams,
} from 'types/Relatorios/Intersemanal';
import {
  SemanalVisaoGeral,
  SemanalVisaoGeralParams,
  SemanalChuva,
  SemanalCarga,
  SemanalResultados,
  SemanalPremissas,
  SemanalSecaoParams,
} from 'types/Relatorios/Semanal';
import { MatrizChuvaObservada, MatrizSecaoParams } from 'types/Relatorios/Matriz';
import {
  MensalVisaoGeral,
  MensalVisaoGeralParams,
  MensalChuva,
  MensalSimilaridadeClimatica,
  MensalSimilaridadeClimaticaParams,
  MensalCarga,
  MensalResultados,
  MensalPremissas,
  MensalSecaoParams,
} from 'types/Relatorios/Mensal';
import baseApi from '../base';

const MOCK_URL = process.env.REACT_APP_MOCK_API_URL;

// MOCK_URL setada → URL absoluta pro json-server (fetchBaseQuery ignora o baseUrl).
// MOCK_URL vazia  → URL relativa → baseApi usa REACT_APP_AMPERE_API_URL (API real).
export const relatorioUrl = (informe: RelatorioPathInforme, path: string): string => (MOCK_URL
  ? `${MOCK_URL}/produtos/relatorios/${informe}/${path}`
  : `produtos/relatorios/${informe}/${path}`);

// Imagem de mapa (path root-relative do contrato, ex.: '/static/maps/x.png').
export const decompMediaUrl = (path: string): string => (MOCK_URL ? `${MOCK_URL}${path}` : path);

// Variante da edição: `{cenario}` XOR `{modo}`, sem chaves `undefined` na query/body.
export const varianteParams = (
  p: { cenario?: string; modo?: string },
): { cenario: string } | { modo: string } | Record<string, never> => {
  if (p.cenario !== undefined) return { cenario: p.cenario };
  if (p.modo !== undefined) return { modo: p.modo };
  return {};
};

const chaveAprovacao = (p: AprovacaoParams): string => (
  `${p.informe}|${p.data_rodada}|${p.cenario ?? p.modo ?? ''}`
);

// A aprovação também é marcada pelo DIA (sem a variante): quem muda o dia inteiro (o texto das
// Premissas Gerais do DESSEM) invalida a aprovação de todas as etapas de uma vez.
const chaveAprovacaoDoDia = (p: AprovacaoParams): string => `${p.informe}|${p.data_rodada}`;

const tagsAposMutacao = (p: AprovacaoParams) => [
  { type: 'RelatorioAprovacao' as const, id: chaveAprovacao(p) },
  { type: 'RelatorioAprovacaoCalendario' as const, id: p.informe },
];

// Premissas Gerais do DESSEM: a seção e o estado do editor são marcados pelo dia (`data_rodada`),
// porque o texto editado vale para as 3 etapas do dia (contrato §2.15 do BE). Tags registradas aqui
// com `enhanceEndpoints` (as outras ficam em `base.tsx`).
const TAG_DESSEM_PREMISSAS = 'RelatorioDessemPremissas' as const;
const TAG_DESSEM_PREMISSAS_EDICAO = 'RelatorioDessemPremissasEdicao' as const;

// Reabrir tira a etapa do snapshot publicado. No DESSEM as Premissas voltam a sair do texto do dia,
// que pode ter sido editado em outra etapa enquanto esta estava publicada: a seção é lida de novo.
const tagsAposReabrir = (p: AprovacaoParams) => (
  p.informe === 'dessem'
    ? [...tagsAposMutacao(p), { type: TAG_DESSEM_PREMISSAS, id: p.data_rodada }]
    : tagsAposMutacao(p)
);

// Gravar ou restaurar o texto do dia recarrega a aprovação (o backend grava o evento "editado" em
// cada etapa não publicada do dia) e o calendário, a seção e o editor de todas as etapas do dia.
const tagsAposEdicaoPremissas = (p: DessemSecaoParams) => {
  const aprovacao: AprovacaoParams = { informe: 'dessem', data_rodada: p.data_rodada, cenario: p.cenario };
  return [
    ...tagsAposMutacao(aprovacao),
    { type: 'RelatorioAprovacao' as const, id: chaveAprovacaoDoDia(aprovacao) },
    { type: TAG_DESSEM_PREMISSAS, id: p.data_rodada },
    { type: TAG_DESSEM_PREMISSAS_EDICAO, id: p.data_rodada },
  ];
};

export const relatoriosApi = baseApi.enhanceEndpoints({
  addTagTypes: [TAG_DESSEM_PREMISSAS, TAG_DESSEM_PREMISSAS_EDICAO],
}).injectEndpoints({
  endpoints: (build) => ({
    getDecompOptions: build.query<DecompOptions, void>({
      query: () => ({
        url: relatorioUrl('decomp', 'options'),
        params: { product_key: getProductKey('relatorios') },
      }),
      transformResponse: transformDecompOptionsResponse,
    }),
    getDecompInformeDiario: build.query<DecompInformeDiario, DecompReportParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'informe-diario'),
        method: 'POST',
        params: { product_key: getProductKey('relatorios') },
        body: { data_rodada: params.data_rodada, submercado: params.submercado },
      }),
      transformResponse: transformDecompInformeDiarioResponse,
    }),
    getDecompPremissas: build.query<DecompPremissas, DecompPremissasParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'premissas-gerais'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompPremissasResponse,
    }),
    getDecompCarga: build.query<DecompCarga, DecompSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'carga'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompCargaResponse,
    }),
    getDecompGeracao: build.query<DecompGeracao, DecompSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'geracao-renovavel-unsi'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompGeracaoResponse,
    }),
    getDecompResultados: build.query<DecompResultados, DecompSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'resultados'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompResultadosResponse,
    }),
    getDecompVisaoGeral: build.query<DecompVisaoGeral, DecompSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'visao-geral'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompVisaoGeralResponse,
    }),
    getDecompEmpilhamento: build.query<DecompEmpilhamento, DecompSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'empilhamento-custos'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompEmpilhamentoResponse,
    }),
    getDecompChuvaObservada: build.query<DecompChuva, DecompSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'chuva-observada'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompChuvaResponse,
    }),
    getDecompChuvaPrevista: build.query<DecompChuva, DecompSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('decomp', 'chuva-prevista'),
        params: { product_key: getProductKey('relatorios'), data_rodada: params.data_rodada, modo: params.modo },
      }),
      transformResponse: transformDecompChuvaResponse,
    }),
    getDessemOptions: build.query<DessemOptions, DessemOptionsParams | void>({
      query: (arg) => ({
        url: relatorioUrl('dessem', 'options'),
        params: {
          product_key: getProductKey('relatorios'),
          ...(arg?.data_rodada ? { data_rodada: arg.data_rodada } : {}),
        },
      }),
      transformResponse: transformDessemOptionsResponse,
    }),
    getDessemPremissas: build.query<DessemPremissas, DessemSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'premissas-gerais'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformDessemPremissasResponse,
      // Só esta seção leva tag (as outras não mudam com a edição do texto do dia).
      providesTags: (_result, _error, params) => [
        { type: TAG_DESSEM_PREMISSAS, id: params.data_rodada },
      ],
    }),
    getDessemCarga: build.query<DessemCarga, DessemSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'carga'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformDessemCargaResponse,
    }),
    getDessemCargaLiquida: build.query<DessemCargaLiquida, DessemSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'carga-liquida'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformDessemCargaLiquidaResponse,
    }),
    getDessemGeracaoRenovavel: build.query<DessemGeracaoRenovavel, DessemSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'geracao-renovavel'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformDessemGeracaoRenovavelResponse,
    }),
    getDessemVisaoGeral: build.query<DessemVisaoGeral, DessemSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'visao-geral'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformDessemVisaoGeralResponse,
    }),
    getDessemResultados: build.query<DessemResultados, DessemSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'resultados'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformDessemResultadosResponse,
    }),
    getRelatorioAprovacao: build.query<AprovacaoStatus, AprovacaoParams>({
      query: ({ informe, data_rodada: dataRodada, ...variante }) => ({
        url: relatorioUrl(informe, 'aprovacao'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: dataRodada,
          ...varianteParams(variante),
        },
      }),
      transformResponse: transformAprovacaoResponse,
      providesTags: (_result, _error, params) => [
        { type: 'RelatorioAprovacao', id: chaveAprovacao(params) },
        { type: 'RelatorioAprovacao', id: chaveAprovacaoDoDia(params) },
      ],
    }),
    getRelatorioAprovacaoCalendario: build.query<Calendario, CalendarioParams>({
      query: ({ informe, mes, ...variante }) => ({
        url: relatorioUrl(informe, 'aprovacao/calendario'),
        params: {
          product_key: getProductKey('relatorios'),
          mes,
          ...varianteParams(variante),
        },
      }),
      transformResponse: transformCalendarioResponse,
      providesTags: (_result, _error, params) => [
        {
          type: 'RelatorioAprovacaoCalendario',
          id: `${params.informe}|${params.mes}|${params.cenario ?? params.modo ?? ''}`,
        },
        { type: 'RelatorioAprovacaoCalendario', id: params.informe },
      ],
    }),
    aprovarRelatorioSecoes: build.mutation<AprovacaoStatus, AprovacaoSecoesBody>({
      query: ({ informe, ...body }) => ({
        url: relatorioUrl(informe, 'aprovacao/aprovar'),
        method: 'POST',
        params: { product_key: getProductKey('relatorios') },
        body,
      }),
      transformResponse: transformAprovacaoResponse,
      invalidatesTags: (_result, _error, params) => tagsAposMutacao(params),
    }),
    revogarRelatorioSecoes: build.mutation<AprovacaoStatus, AprovacaoSecoesBody>({
      query: ({ informe, ...body }) => ({
        url: relatorioUrl(informe, 'aprovacao/revogar'),
        method: 'POST',
        params: { product_key: getProductKey('relatorios') },
        body,
      }),
      transformResponse: transformAprovacaoResponse,
      invalidatesTags: (_result, _error, params) => tagsAposMutacao(params),
    }),
    publicarRelatorio: build.mutation<AprovacaoStatus, AprovacaoParams>({
      query: ({ informe, ...body }) => ({
        url: relatorioUrl(informe, 'aprovacao/publicar'),
        method: 'POST',
        params: { product_key: getProductKey('relatorios') },
        body,
      }),
      transformResponse: transformAprovacaoResponse,
      invalidatesTags: (_result, _error, params) => tagsAposMutacao(params),
    }),
    reabrirRelatorio: build.mutation<AprovacaoStatus, AprovacaoParams>({
      query: ({ informe, ...body }) => ({
        url: relatorioUrl(informe, 'aprovacao/reabrir'),
        method: 'POST',
        params: { product_key: getProductKey('relatorios') },
        body,
      }),
      transformResponse: transformAprovacaoResponse,
      invalidatesTags: (_result, _error, params) => tagsAposReabrir(params),
    }),
    // Edição das Premissas Gerais do DESSEM (só admin; contrato §2.15 do BE).
    // `keepUnusedDataFor: 0`: o modal sempre abre com o estado atual (bloqueio, autoria), nunca
    // com o de uma abertura anterior.
    getDessemPremissasEdicao: build.query<DessemPremissasEdicao, DessemSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'aprovacao/premissas'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformDessemPremissasEdicaoResponse,
      providesTags: (_result, _error, params) => [
        { type: TAG_DESSEM_PREMISSAS_EDICAO, id: params.data_rodada },
      ],
      keepUnusedDataFor: 0,
    }),
    // Body montado campo a campo: vai exatamente `{data_rodada, cenario, conteudo_markdown}`, com o
    // texto como o admin escreveu (sem `trim`).
    editarDessemPremissas: build.mutation<AprovacaoStatus, DessemEditarPremissasBody>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'aprovacao/editar-premissas'),
        method: 'POST',
        params: { product_key: getProductKey('relatorios') },
        body: {
          data_rodada: params.data_rodada,
          cenario: params.cenario,
          conteudo_markdown: params.conteudo_markdown,
        },
      }),
      transformResponse: transformAprovacaoResponse,
      invalidatesTags: (_result, _error, params) => tagsAposEdicaoPremissas(params),
    }),
    restaurarDessemPremissas: build.mutation<AprovacaoStatus, DessemRestaurarPremissasBody>({
      query: (params) => ({
        url: relatorioUrl('dessem', 'aprovacao/restaurar-premissas'),
        method: 'POST',
        params: { product_key: getProductKey('relatorios') },
        body: { data_rodada: params.data_rodada, cenario: params.cenario },
      }),
      transformResponse: transformAprovacaoResponse,
      invalidatesTags: (_result, _error, params) => tagsAposEdicaoPremissas(params),
    }),
    getIntersemanalVisaoGeral: build.query<IntersemanalVisaoGeral, IntersemanalVisaoGeralParams>({
      query: (params) => ({
        url: relatorioUrl('intersemanal', 'visao-geral'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
          subsistema: params.subsistema,
        },
      }),
      transformResponse: transformIntersemanalVisaoGeralResponse,
    }),
    getIntersemanalChuvaObservada: build.query<IntersemanalChuva, IntersemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('intersemanal', 'chuva-observada'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformIntersemanalChuvaResponse,
    }),
    getIntersemanalChuvaPrevista: build.query<IntersemanalChuva, IntersemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('intersemanal', 'chuva-prevista'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformIntersemanalChuvaResponse,
    }),
    getIntersemanalCarga: build.query<IntersemanalCarga, IntersemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('intersemanal', 'carga'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformIntersemanalCargaResponse,
    }),
    getIntersemanalResultados: build.query<IntersemanalResultados, IntersemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('intersemanal', 'resultados'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformIntersemanalResultadosResponse,
    }),
    getIntersemanalPremissas: build.query<IntersemanalPremissas, IntersemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('intersemanal', 'premissas-gerais'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformIntersemanalPremissasResponse,
    }),
    getSemanalVisaoGeral: build.query<SemanalVisaoGeral, SemanalVisaoGeralParams>({
      query: (params) => ({
        url: relatorioUrl('semanal', 'visao-geral'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
          subsistema: params.subsistema,
        },
      }),
      transformResponse: transformSemanalVisaoGeralResponse,
    }),
    getSemanalChuvaObservada: build.query<SemanalChuva, SemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('semanal', 'chuva-observada'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformSemanalChuvaResponse,
    }),
    getSemanalChuvaPrevista: build.query<SemanalChuva, SemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('semanal', 'chuva-prevista'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformSemanalChuvaResponse,
    }),
    getSemanalCarga: build.query<SemanalCarga, SemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('semanal', 'carga'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformSemanalCargaResponse,
    }),
    getSemanalResultados: build.query<SemanalResultados, SemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('semanal', 'resultados'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformSemanalResultadosResponse,
    }),
    getSemanalPremissas: build.query<SemanalPremissas, SemanalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('semanal', 'premissas-gerais'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformSemanalPremissasResponse,
    }),
    getMatrizChuvaObservada: build.query<MatrizChuvaObservada, MatrizSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('matriz', 'chuva-observada'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
          matriz: params.matriz,
          mes: params.mes,
          subsistema: params.subsistema,
        },
      }),
      transformResponse: transformMatrizChuvaObservadaResponse,
    }),
    getMensalVisaoGeral: build.query<MensalVisaoGeral, MensalVisaoGeralParams>({
      query: (params) => ({
        url: relatorioUrl('mensal', 'visao-geral'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
          subsistema: params.subsistema,
        },
      }),
      transformResponse: transformMensalVisaoGeralResponse,
    }),
    getMensalChuvaObservada: build.query<MensalChuva, MensalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('mensal', 'chuva-observada'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformMensalChuvaResponse,
    }),
    getMensalChuvaPrevista: build.query<MensalChuva, MensalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('mensal', 'chuva-prevista'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformMensalChuvaResponse,
    }),
    getMensalSimilaridadeClimatica: build.query<
    MensalSimilaridadeClimatica, MensalSimilaridadeClimaticaParams
    >({
      query: (params) => ({
        url: relatorioUrl('mensal', 'similaridade-climatica'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
          subsistema: params.subsistema,
        },
      }),
      transformResponse: transformMensalSimilaridadeClimaticaResponse,
    }),
    getMensalCarga: build.query<MensalCarga, MensalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('mensal', 'carga'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformMensalCargaResponse,
    }),
    getMensalResultados: build.query<MensalResultados, MensalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('mensal', 'resultados'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformMensalResultadosResponse,
    }),
    getMensalPremissas: build.query<MensalPremissas, MensalSecaoParams>({
      query: (params) => ({
        url: relatorioUrl('mensal', 'premissas-gerais'),
        params: {
          product_key: getProductKey('relatorios'),
          data_rodada: params.data_rodada,
          cenario: params.cenario,
        },
      }),
      transformResponse: transformMensalPremissasResponse,
    }),
  }),
});

export const {
  useGetDecompOptionsQuery,
  useGetDecompInformeDiarioQuery,
  useGetDecompPremissasQuery,
  useGetDecompCargaQuery,
  useGetDecompGeracaoQuery,
  useGetDecompResultadosQuery,
  useGetDecompVisaoGeralQuery,
  useGetDecompEmpilhamentoQuery,
  useGetDecompChuvaObservadaQuery,
  useGetDecompChuvaPrevistaQuery,
  useGetDessemOptionsQuery,
  useGetDessemPremissasQuery,
  useGetDessemCargaQuery,
  useGetDessemCargaLiquidaQuery,
  useGetDessemGeracaoRenovavelQuery,
  useGetDessemVisaoGeralQuery,
  useGetDessemResultadosQuery,
  useGetRelatorioAprovacaoQuery,
  useGetRelatorioAprovacaoCalendarioQuery,
  useAprovarRelatorioSecoesMutation,
  useRevogarRelatorioSecoesMutation,
  usePublicarRelatorioMutation,
  useReabrirRelatorioMutation,
  useGetDessemPremissasEdicaoQuery,
  useEditarDessemPremissasMutation,
  useRestaurarDessemPremissasMutation,
  useGetIntersemanalVisaoGeralQuery,
  useGetIntersemanalChuvaObservadaQuery,
  useGetIntersemanalChuvaPrevistaQuery,
  useGetIntersemanalCargaQuery,
  useGetIntersemanalResultadosQuery,
  useGetIntersemanalPremissasQuery,
  useGetSemanalVisaoGeralQuery,
  useGetSemanalChuvaObservadaQuery,
  useGetSemanalChuvaPrevistaQuery,
  useGetSemanalCargaQuery,
  useGetSemanalResultadosQuery,
  useGetSemanalPremissasQuery,
  useGetMatrizChuvaObservadaQuery,
  useGetMensalVisaoGeralQuery,
  useGetMensalChuvaObservadaQuery,
  useGetMensalChuvaPrevistaQuery,
  useGetMensalSimilaridadeClimaticaQuery,
  useGetMensalCargaQuery,
  useGetMensalResultadosQuery,
  useGetMensalPremissasQuery,
} = relatoriosApi;
